export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export type TourStepId =
  | 'map_intro'
  | 'search'
  | 'locate'
  | 'menu'
  | 'view_modes'
  | 'legend'
  | 'fab'
  | 'finish';

export interface TourStep {
  id: TourStepId;
  target: string;
  title: string;
  body: string;
  placement: TourPlacement;
  beforeShow?: () => void;
}

export interface HelpTipContent {
  title: string;
  body: string;
}

export type TipKey =
  | 'search'
  | 'filters'
  | 'view_modes'
  | 'legend'
  | 'fab'
  | 'form_spot'
  | 'form_seeker'
  | 'form_rent';

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'map_intro',
    target: '[data-tour="map"]',
    title: "India's parking map",
    body: 'Green = free spots · Amber = paid · Blue = seekers · Purple = price data. Tap any pin to see details and share on WhatsApp.',
    placement: 'center',
  },
  {
    id: 'search',
    target: '[data-tour="search"]',
    title: 'Jump to any place',
    body: "Type a metro station, area, or landmark. Backed by Ola Maps for India-first coverage.",
    placement: 'bottom',
  },
  {
    id: 'locate',
    target: '[data-tour="locate"]',
    title: 'See parking near you',
    body: 'We use your location only to find spots within 3 km. We never ask you to sign up.',
    placement: 'bottom',
  },
  {
    id: 'menu',
    target: '[data-tour="menu"]',
    title: 'The menu',
    body: 'Open it for stats, filters, and a switcher for what shows on the map.',
    placement: 'bottom',
    beforeShow: undefined,
  },
  {
    id: 'view_modes',
    target: '[data-tour="view-modes"]',
    title: 'Switch map layers',
    body: 'All shows everything. Spots for owners, Seekers for demand, Prices for the transparency data layer.',
    placement: 'right',
    beforeShow: undefined,
  },
  {
    id: 'legend',
    target: '[data-tour="legend-dropdown"]',
    title: 'Pin legend',
    body: 'Free (green) and Paid (amber) are parking spots. Seeker (blue) is someone looking for a spot. Price Data (purple) is reported pricing.',
    placement: 'right',
    beforeShow: undefined,
  },
  {
    id: 'fab',
    target: '[data-tour="fab"]',
    title: 'Three ways to contribute',
    body: 'All three are anonymous and free. Tap any option above to contribute.',
    placement: 'left',
  },
  {
    id: 'finish',
    target: '',
    title: "You're set",
    body: 'Parksy is free forever, no brokerage, no signup. If you find a wrong pin, tap the flag on its popup. Tap the ? anytime to revisit this tour.',
    placement: 'center',
  },
];

export const HELP_TIPS: Record<TipKey, HelpTipContent> = {
  search: {
    title: 'Search examples',
    body: "Try 'MG Road', 'Andheri station', or your society name. Free-text landmarks also work.",
  },
  filters: {
    title: 'What filters do',
    body: "Filters narrow what's visible on the map. They don't affect other tabs. Tap Clear all to reset.",
  },
  view_modes: {
    title: 'Layer meanings',
    body: 'All = everything · Spots = parking for rent · Seekers = people looking · Prices = what people pay (data only, not for rent)',
  },
  legend: {
    title: 'Reading the map',
    body: 'Green = free, amber = paid, blue = seeker, purple = price data. A glowing cluster means multiple pins — zoom in to expand them.',
  },
  fab: {
    title: 'Add a pin',
    body: "Three options. You can drop a seeker pin without listing anything yourself.",
  },
  form_spot: {
    title: 'Listing a spot',
    body: 'For paid parking, all three prices (hourly, daily, monthly) are required so seekers can filter on any duration. Free spots only need the toggle.',
  },
  form_seeker: {
    title: 'Finding parking',
    body: "Drop this pin at the exact spot you need parking. We'll email you when a matching spot opens within 3 km. Your pin auto-expires after 30 days.",
  },
  form_rent: {
    title: 'Price transparency',
    body: 'This pin is data-only — it does not mean you have a spot to rent. It helps others know what people actually pay in this area.',
  },
};

export function getStepIndex(id: TourStepId): number {
  return TOUR_STEPS.findIndex((s) => s.id === id);
}
